import { authGuardFunction } from '@bmw-fedev/auth';
import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { canDeactivateGuard } from './shared/guards/can-deactivate.guard';
import { featureFlagCanMatch } from './shared/services/feature-flag.service';
import { roleGuard } from './shared/guards/role.guard';
import { Roles } from './shared/enums/roles.enum';
import {
  commentsHistoryTitle,
  damageIdTitle,
  vehicleMovementsTitle,
} from './shared/title/title.resolvers';
import { provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { claimLandingGuard } from './claims/shell/claim-landing.guard';
import { recoveryTabGuard } from './claims/recovery/recovery-tab.guard';
import { claimWorkflowEffects } from './claims/state/claim-workflow.effects';
import { claimWorkflowFeature } from './claims/state/claim-workflow.feature';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canMatch: [
      () => {
        const redirect = sessionStorage.getItem('tqc-post-auth-redirect');
        if (redirect) {
          sessionStorage.removeItem('tqc-post-auth-redirect');
          return inject(Router).parseUrl(redirect);
        }
        return false;
      },
    ],
    children: [],
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'session-expired',
    loadComponent: () =>
      import('./common/session-expired/session-expired.component').then(
        (m) => m.SessionExpiredComponent
      ),
  },
  {
    path: '',
    canActivate: [authGuardFunction],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'recovery/bordereau',
        data: {
          breadcrumb: 'navigation.recoveries.bordereau',
        },
        loadComponent: () =>
          import('./claims/recovery-bordereau/recovery-bordereau.component').then(
            (m) => m.RecoveryBordereauComponent
          ),
      },
      {
        path: 'settlement/bordereau',
        data: {
          breadcrumb: 'navigation.settlements.bordereau',
        },
        loadComponent: () =>
          import('./claims/settlement-bordereau/settlement-bordereau.component').then(
            (m) => m.SettlementBordereauComponent
          ),
      },
      {
        path: 'damages',
        data: {
          roles: [Roles.SUPER_USER, Roles.DAMAGE_ENTRY],
          breadcrumb: 'damages.breadcrumb.homeLabel',
        },
        canActivate: [roleGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./damages/damages.component').then(
                (m) => m.DamagesComponent
              ),
          },
          {
            path: 'damage-entry',
            data: {
              breadcrumb: 'damages.breadcrumb.singleEntryLabel',
            },
            loadComponent: () =>
              import('./damages/damage-entry-layout/damage-entry-layout.component').then(
                (m) => m.DamageEntryLayoutComponent
              ),
            canDeactivate: [canDeactivateGuard],
          },
          {
            path: 'view-damage-entry',
            data: {
              readOnly: true,
              breadcrumb: 'damages.breadcrumb.singleEntryLabel',
            },
            loadComponent: () =>
              import('./damages/damage-entry-layout/damage-entry-layout.component').then(
                (m) => m.DamageEntryLayoutComponent
              ),
          },
          {
            path: 'batch-upload',
            data: {
              breadcrumb: 'damageEntry.forms.batchEntry',
            },
            loadComponent: () =>
              import('./damages/components/batch-upload/batch-upload.component').then(
                (m) => m.BatchUploadComponent
              ),
          },
        ],
      },
      {
        path: 'recoveries',
        children: [
          {
            path: 'working-queues',
            data: {
              breadcrumb: [
                'breadcrumbs.workingQueues',
                'dashboard.headings.recovery',
              ],
            },
            loadComponent: () =>
              import('./recoveries/working-queue/working-queue.component').then(
                (m) => m.RecoveriesWorkingQueuesComponent
              ),
          },
        ],
      },
      {
        path: 'liabilities',
        children: [
          {
            path: 'working-queues',
            data: {
              breadcrumb: [
                'breadcrumbs.workingQueues',
                'dashboard.headings.liability',
              ],
            },
            loadComponent: () =>
              import('./liabilities/working-queues/working-queues.component').then(
                (m) => m.LiabilitiesWorkingQueuesComponent
              ),
          },
          {
            path: ':id',
            title: damageIdTitle,
            loadComponent: () =>
              import('./liabilities/liability-page/liability-page.component').then(
                (m) => m.LiabilityPageComponent
              ),
          },
        ],
      },
      {
        path: 'vin',
        children: [
          {
            path: ':vin',
            data: {
              breadcrumb: { param: 'vin' },
            },
            children: [
              {
                path: '',
                canMatch: [featureFlagCanMatch('newVinOverview')],
                loadComponent: () =>
                  import('./vin/vin-overview/vin-overview.component').then(
                    (m) => m.VinOverviewComponent
                  ),
              },
              {
                path: '',
                loadComponent: () =>
                  import('./vin-legacy/vin.component').then(
                    (m) => m.VinComponent
                  ),
              },
              {
                path: 'history',
                data: {
                  breadcrumb: 'damageEntry.damageHistories.sectionHeading',
                },
                loadComponent: () =>
                  import('./vin/damage-entry-history/damage-entry-history.component').then(
                    (m) => m.DamageEntryHistoryComponent
                  ),
              },
              {
                path: 'comments',
                title: commentsHistoryTitle,
                data: {
                  breadcrumb: 'vin.commentsHistory.heading',
                },
                loadComponent: () =>
                  import('./vin/comments-history/comments-history.component').then(
                    (m) => m.CommentsHistoryComponent
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'claims',
        children: [
          {
            path: 'working-queues',
            data: {
              breadcrumb: [
                'breadcrumbs.workingQueues',
                'dashboard.headings.settlement',
              ],
            },
            loadComponent: () =>
              import('./claims/working-queues/working-queues.component').then(
                (m) => m.ClaimsWorkingQueuesComponent
              ),
          },
          {
            path: ':id/settlement/cost-types/new',
            canDeactivate: [canDeactivateGuard],
            providers: [
              provideState(claimWorkflowFeature),
              provideEffects(claimWorkflowEffects),
            ],
            loadComponent: () =>
              import('./claims/cost-types/cost-type-details/cost-type-details.component').then(
                (m) => m.CostTypeDetailsComponent
              ),
          },
          {
            path: ':id/settlement/cost-types/:costTypeId',
            canDeactivate: [canDeactivateGuard],
            providers: [
              provideState(claimWorkflowFeature),
              provideEffects(claimWorkflowEffects),
            ],
            loadComponent: () =>
              import('./claims/cost-types/cost-type-details/cost-type-details.component').then(
                (m) => m.CostTypeDetailsComponent
              ),
          },
          {
            path: ':id/recovery/:recoveryId/cost-types/new',
            canActivate: [recoveryTabGuard],
            canDeactivate: [canDeactivateGuard],
            providers: [
              provideState(claimWorkflowFeature),
              provideEffects(claimWorkflowEffects),
            ],
            loadComponent: () =>
              import('./claims/cost-types/cost-type-details/cost-type-details.component').then(
                (m) => m.CostTypeDetailsComponent
              ),
          },
          {
            path: ':id/recovery/:recoveryId/cost-types/:costTypeId',
            canActivate: [recoveryTabGuard],
            canDeactivate: [canDeactivateGuard],
            providers: [
              provideState(claimWorkflowFeature),
              provideEffects(claimWorkflowEffects),
            ],
            loadComponent: () =>
              import('./claims/cost-types/cost-type-details/cost-type-details.component').then(
                (m) => m.CostTypeDetailsComponent
              ),
          },
          {
            path: ':id',
            providers: [
              provideState(claimWorkflowFeature),
              provideEffects(claimWorkflowEffects),
            ],
            loadComponent: () =>
              import('./claims/shell/claims-container/claims-container.component').then(
                (m) => m.ClaimsContainerComponent
              ),
            children: [
              {
                path: '',
                pathMatch: 'full',
                canActivate: [claimLandingGuard],
                children: [],
              },
              {
                path: 'settlement',
                loadComponent: () =>
                  import('./claims/settlement/settlement-tab/settlement-tab.component').then(
                    (m) => m.SettlementTabComponent
                  ),
              },
              {
                path: 'recovery',
                canActivate: [recoveryTabGuard],
                loadComponent: () =>
                  import('./claims/recovery/recovery-tab/recovery-tab.component').then(
                    (m) => m.RecoveryTabComponent
                  ),
              },
              {
                path: 'uploads',
                loadComponent: () =>
                  import('./claims/uploads/uploads-tab/uploads-tab.component').then(
                    (m) => m.UploadsTabComponent
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'vehicleMovements',
        title: vehicleMovementsTitle,
        data: {
          roles: [
            Roles.SUPER_USER,
            Roles.DAMAGE_ENTRY,
            Roles.REPAIR_SHOP,
            Roles.SURVEYOR,
            Roles.CLAIM_HANDLER_SETTLEMENT,
            Roles.CLAIM_HANDLER_RECOVERY,
            Roles.PAYER_OF_DAMAGE_BUSINESS,
            Roles.PAYER_OF_DAMAGE_FINANCE,
            Roles.REPORTING_ALL_COUNTRIES,
            Roles.READING_RIGHTS,
          ],
          breadcrumb: 'administration.vehicleMovements.breadcrumb.label',
        },
        canActivate: [roleGuard],
        loadComponent: () =>
          import('./admin/vehicle-movements/vehicle-movements.component').then(
            (m) => m.VehicleMovementsComponent
          ),
      },
      {
        path: 'reporting',
        data: {
          breadcrumb: 'navigation.reporting',
        },
        loadComponent: () =>
          import('./reporting/reporting.component').then(
            (m) => m.ReportingComponent
          ),
      },
      {
        path: 'admin',
        data: {
          roles: [
            Roles.SUPER_USER,
            Roles.ADMIN_COUNTRY,
            Roles.ADMIN_LOGISTIC_PARTNER,
          ],
          breadcrumb: 'administration.breadcrumb.label',
        },
        canActivate: [roleGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./admin/admin.component').then((m) => m.AdminComponent),
          },
          {
            path: 'users',
            data: {
              breadcrumb: 'administration.users.breadcrumb.label',
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./admin/users/users.component').then(
                    (m) => m.UsersComponent
                  ),
              },
              {
                path: ':userId',
                loadComponent: () =>
                  import('./admin/users/user-profile/user-profile.component').then(
                    (m) => m.UserProfileComponent
                  ),
              },
            ],
          },
          {
            path: 'translations',
            data: {
              roles: [Roles.SUPER_USER, Roles.ADMIN_COUNTRY],
              breadcrumb: 'administration.translations.breadcrumb.label',
            },
            canActivate: [roleGuard],
            loadComponent: () =>
              import('./admin/translations/translations.component').then(
                (m) => m.TranslationsComponent
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./admin/translations/create-namespace/create-namespace.component').then(
                    (m) => m.TranslationsCreateNamespaceComponent
                  ),
              },
              {
                path: ':namespace',
                loadComponent: () =>
                  import('./admin/translations/edit-namespace/edit-namespace.component').then(
                    (m) => m.TranslationsEditNamespaceComponent
                  ),
              },
            ],
          },
          {
            path: 'locations',
            data: {
              roles: [Roles.SUPER_USER],
              breadcrumb: 'administration.locations.breadcrumb.label',
            },
            canActivate: [roleGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./admin/locations/locations.component').then(
                    (m) => m.LocationsComponent
                  ),
              },
              {
                path: ':id',
                children: [
                  {
                    path: '',
                    data: {
                      breadcrumb:
                        'administration.locations.locationDetails.breadcrumb.label',
                    },
                    loadComponent: () =>
                      import('./admin/locations/location-details/location-details.component').then(
                        (m) => m.LocationDetailsComponent
                      ),
                  },
                  {
                    path: 'surveyPoints',
                    data: {
                      breadcrumb:
                        'administration.locations.locationDetails.surveyPoints.breadcrumb.label',
                    },
                    loadComponent: () =>
                      import('./admin/locations/location-details/survey-points/survey-point-details/survey-point-details.component').then(
                        (m) => m.SurveyPointDetailsComponent
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'process-partners',
            data: {
              roles: [Roles.SUPER_USER],
              breadcrumb: 'administration.processPartners.breadcrumb.label',
            },
            canActivate: [roleGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./admin/process-partners/process-partners.component').then(
                    (m) => m.ProcessPartnersComponent
                  ),
              },
              {
                path: 'carrier',
                data: {
                  breadcrumb:
                    'administration.processPartners.carrier.breadcrumb.label',
                },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./admin/process-partners/carriers/carriers.component').then(
                        (m) => m.CarriersComponent
                      ),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/carriers/carriers.component').then(
                        (m) => m.CarriersComponent
                      ),
                  },
                ],
              },
              {
                path: 'claimHandler',
                data: {
                  breadcrumb:
                    'administration.processPartners.claimHandler.breadcrumb.label',
                },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./admin/process-partners/claim-handler/claim-handler.component').then(
                        (m) => m.ClaimHandlerComponent
                      ),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/claim-handler/claim-handler.component').then(
                        (m) => m.ClaimHandlerComponent
                      ),
                  },
                ],
              },
              {
                path: 'bearerOfRisk',
                data: {
                  breadcrumb:
                    'administration.processPartners.bearerOfRisk.breadcrumb.label',
                },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./admin/process-partners/bearer-of-risk/bearer-of-risk.component').then(
                        (m) => m.BearerOfRiskComponent
                      ),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/bearer-of-risk/bearer-of-risk.component').then(
                        (m) => m.BearerOfRiskComponent
                      ),
                  },
                ],
              },
              {
                path: 'surveyor',
                data: {
                  breadcrumb:
                    'administration.processPartners.surveyor.breadcrumb.label',
                },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./admin/process-partners/surveyor/surveyor.component').then(
                        (m) => m.SurveyorComponent
                      ),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/surveyor/surveyor.component').then(
                        (m) => m.SurveyorComponent
                      ),
                  },
                ],
              },
              {
                path: 'plant',
                data: {
                  breadcrumb:
                    'administration.processPartners.plant.breadcrumb.label',
                },
                children: [
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/plant/plant.component').then(
                        (m) => m.PlantComponent
                      ),
                  },
                ],
              },
              {
                path: 'dealer',
                data: {
                  breadcrumb:
                    'administration.processPartners.dealer.breadcrumb.label',
                },
                children: [
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/dealer/dealer.component').then(
                        (m) => m.DealerComponent
                      ),
                  },
                ],
              },
              {
                path: 'gcAgent',
                data: {
                  breadcrumb:
                    'administration.processPartners.gcAgent.breadcrumb.label',
                },
                children: [
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/process-partners/gc-agent/gc-agent.component').then(
                        (m) => m.GcAgentComponent
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'partsManager',
            data: {
              roles: [Roles.SUPER_USER, Roles.ADMIN_COUNTRY],
              breadcrumb: 'administration.partsManager.breadcrumb.label',
            },
            canActivate: [roleGuard],
            loadComponent: () =>
              import('./admin/parts-manager/parts-manager.component').then(
                (m) => m.PartsManagerComponent
              ),
          },
          {
            path: 'countries',
            data: {
              breadcrumb: 'administration.countries.breadcrumb.label',
            },
            loadComponent: () =>
              import('./admin/countries/countries.component').then(
                (m) => m.CountriesComponent
              ),
            loadChildren: () =>
              import('./admin/countries/countries.routes').then(
                (m) => m.countryRoutes
              ),
          },
        ],
      },
    ],
  },
  // {
  //   path: '**',
  //   redirectTo: '/dashboard',
  // },
];
